/**
 *
 * @module bamWebPlayerAdapter
 * @desc PlayerAdapter for the web-based bam-video-players
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see https://github.bamtech.co/fed-app-media-platform/bam-video-players/tree/master/jsdoc
 * @see https://github.bamtech.co/fed-app-media-platform/bam-video-players/tree/master/jsdoc#playerevents--enum
 * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/production-drm/API.md
 * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/production-drm/API.md#drm-configuration
 * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/production-drm/demo/index.html
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;
const DrmType = Services.Media.DrmType;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    assigned,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class BamWebPlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Interface used to communicate with the media player.
 * @note BAM internal web player adapter
 *
 */
export default class BamWebPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer An instance of the web-based bam-video-players
     * @throws {InvalidArgumentException}
     * @note The BamWebPlayerAdapter requires nativePlayer.dispatcher
     *
     */
    constructor(nativePlayer) {

        const errorMessage = {
            ctor: 'BamWebPlayerAdapter(nativePlayer)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            }
        };

        assigned(Util.getSafe(() => nativePlayer.dispatcher), errorMessage.nativePlayer, InvalidArgumentException); // eslint-disable-line

        super(nativePlayer);

        /**
         *
         * @access private
         * @type {Array<SDK.Drm.DrmProvider>}
         * @since 3.2.0
         * @desc Set of DRM providers
         *
         */
        this.drmProviders = [];

    }

    /**
     *
     * @access public
     * @param {String} playlistUri The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @return {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'BamWebPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        return new Promise((resolve) => {

            this.playlistUri = playlistUri;

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @desc Gets a snapshot of information about media playback.
     * @throws {InvalidStateException} Unable to get NativePlayer playhead or bitrate data
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'BamWebPlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            },
            get currentBitrate() {
                return `${this.method} unable to get NativePlayer bitrate data`;
            }
        };

        const { nativePlayer, unavailableMetric } = this;

        if (Check.not.assigned(nativePlayer.bitrates)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        if (Check.not.function(nativePlayer.getCurrentTimeInSecs)) {

            const reasons = [new ErrorReason('', errorMessage.currentBitrate)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = Math.floor(nativePlayer.bitrates.getCurrentKbps()) || unavailableMetric;
        const currentPlayhead = Math.floor(nativePlayer.getCurrentTimeInSecs()) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'BamWebPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers } = this;
        const { onPlay, onPause, onError, onPlayedToCompletion } = this;
        const { dispatcher } = nativePlayer;
        const EventList = nativePlayer.events.getEventList();
        const { PlayerEvents } = EventList;
        const { MEDIA_START, MEDIA_RESUME, MEDIA_PAUSED, MEDIA_ERROR, MEDIA_COMPLETE } = PlayerEvents;

        return new Promise((resolve, reject) => {

            if (Check.assigned(dispatcher)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onPause = onPause.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);

                dispatcher.on(MEDIA_START, boundHandlers.onPlay);
                dispatcher.on(MEDIA_RESUME, boundHandlers.onPlay);
                dispatcher.on(MEDIA_PAUSED, boundHandlers.onPause);
                dispatcher.on(MEDIA_ERROR, boundHandlers.onError);
                dispatcher.on(MEDIA_COMPLETE, boundHandlers.onPlayedToCompletion);

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to remove PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'BamWebPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get removeListener() {
                return `${this.method} unable to remove PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers } = this;
        const { dispatcher } = nativePlayer;
        const EventList = nativePlayer.events.getEventList();
        const { PlayerEvents } = EventList;
        const { MEDIA_START, MEDIA_RESUME, MEDIA_PAUSED, MEDIA_ERROR, MEDIA_COMPLETE } = PlayerEvents;

        return new Promise((resolve, reject) => {

            if (Check.assigned(dispatcher) && Check.equal(this.listener, listener)) {

                this.listener = null;

                dispatcher.off(MEDIA_START, boundHandlers.onPlay);
                dispatcher.off(MEDIA_RESUME, boundHandlers.onPlay);
                dispatcher.off(MEDIA_PAUSED, boundHandlers.onPause);
                dispatcher.off(MEDIA_ERROR, boundHandlers.onError);
                dispatcher.off(MEDIA_COMPLETE, boundHandlers.onPlayedToCompletion);

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.removeListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @throws {InvalidStateException} this PlayerAdapter needs multiple SDK.Drm.DrmProvider instances,
     * please use BamWebPlayerAdapter#setDrmProviders(drmProviders) which takes in an
     * Array of SDK.Drm.DrmProvider instances
     *
     */
    setDrmProvider(drmProvider) { // eslint-disable-line

        const method = 'BamWebPlayerAdapter.setDrmProvider(drmProvider)';
        const exceptionData = ExceptionReference.common.invalidState;

        const reasons = [new ErrorReason('', `
            ${method} - this PlayerAdapter needs multiple SDK.Drm.DrmProvider instances,
            please use BamWebPlayerAdapter#setDrmProviders(drmProviders) which takes in an
            Array of SDK.Drm.DrmProvider instances
        `)];

        return Promise.reject(new ServiceException({ reasons, exceptionData }));

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {Array<SDK.Drm.DrmProvider>} drmProviders
     * @returns {Promise<Void>}
     *
     */
    setDrmProviders(drmProviders) {

        const errorMessage = {
            method: 'BamWebPlayerAdapter.setDrmProviders(drmProviders)',
            get drmProviders() {
                return `${this.method} needs Array of SDK.Drm.DrmProvider`;
            }
        };

        array.of.instanceStrict(drmProviders, DrmProvider, errorMessage.drmProviders, InvalidArgumentException);

        const { nativePlayer, setDrmConfiguration } = this;

        this.drmProviders = drmProviders;

        nativePlayer.utils.drmConfigCallback(setDrmConfiguration.bind(this));

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener, nativePlayer, boundHandlers } = this;
        const { dispatcher } = nativePlayer;
        const EventList = nativePlayer.events.getEventList();
        const { PlayerEvents } = EventList;
        const { MEDIA_START, MEDIA_RESUME, MEDIA_PAUSED, MEDIA_ERROR, MEDIA_COMPLETE } = PlayerEvents;

        dispatcher.off(MEDIA_START, boundHandlers.onPlay);
        dispatcher.off(MEDIA_RESUME, boundHandlers.onPlay);
        dispatcher.off(MEDIA_PAUSED, boundHandlers.onPause);
        dispatcher.off(MEDIA_ERROR, boundHandlers.onError);
        dispatcher.off(MEDIA_COMPLETE, boundHandlers.onPlayedToCompletion);

        this.playlistUri = '';
        this.boundHandlers = {};
        this.drmProvider = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access private
     * @since 3.2.0
     * @desc The DRM Configuration contains an array of key system configurations, one for
     * each supported key system. At a minimum, the key system configuration specifies
     * the key system, and a method to acquire a license, either a uri or a callback function.
     *
     * If a callback is provided, then the license request uri is optional, and would be available to the callback.
     * If no callback is provided, then the licenseRequestUri is not optional.
     *
     * @note licenseRequestHeaders expects a name/value set,
     * i.e. [{name: 'Content-Type', value: 'application/octet-stream'}]
     * @note serverCertifcate is mandatory for FAIRPLAY and recommended for WIDEVINE
     * @note serverCertificate is expected to be a Promise<BufferSource> or BufferSource
     * @see https://github.bamtech.co/bam-hls/bam-hls.js/blob/master/API.md#keysystemconfiguration
     * @returns {Object<Array>}
     *
     */
    setDrmConfiguration() {

        const { drmProviders } = this;
        const keySystems = [];

        for (const drmProvider of drmProviders) {

            const keySystem = drmProvider.type;
            const licenseRequestUri = Util.getSafe(() => drmProvider.licenseRequestUri);

            keySystems.push({
                keySystem,
                licenseRequestUri,
                licenseRequestHeadersCallback: () => drmProvider.formatRequestHeadersList(
                    drmProvider.processLicenseRequestHeaders()
                ),
                /**
                 *
                 * @note bam-hls calls `Promise.resolve(this._keySystemConfig.serverCertificate)`
                 * where serverCertificate is a reference to this function, in EME.js.
                 * We need to use an IIFE here so we setup a promise since they are not executing this function
                 * only expecting a Promise(cert), Promise(undefined), cert or undefined
                 *
                 */
                serverCertificate: (() => {

                    if (keySystem === DrmType.FAIRPLAY) {

                        return drmProvider.getFairPlayCertificate();

                    } else if (keySystem === DrmType.WIDEVINE) {

                        return drmProvider.getWidevineCertificate();

                    }

                    // bam-hls expects undefined so they don't attempt to process this as a certificate
                    return Promise.resolve(undefined);

                })()
            });

        }

        return {
            keySystems
        };

    }

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;

        return Promise.resolve();

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/bamWebPlayerAdapter.js